'use client';

import React from 'react';
import Link from 'next/link';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

export default function Error500Page({
  error,
  reset,
}: {
  error?: Error & { digest?: string };
  reset?: () => void;
}) {
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 text-center">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-2">
            <AppLogo size={32} />
            <span className="text-lg font-bold text-foreground">Wealth<span className="text-primary">OS</span></span>
          </div>
        </div>

        {/* Icon */}
        <div className="relative inline-flex items-center justify-center w-24 h-24 rounded-3xl bg-red-500/10 border border-red-500/20 mb-6 mx-auto">
          <AlertTriangle size={40} className="text-red-500" />
          <span className="absolute -top-2 -right-2 text-3xl font-black text-red-500/20 select-none">500</span>
        </div>

        <h1 className="text-2xl font-bold text-foreground mb-3">Something went wrong</h1>
        <p className="text-sm text-muted-foreground leading-relaxed mb-8 max-w-sm mx-auto">
          We encountered an unexpected error. Our team has been notified and is working to fix it. Please try again in a moment.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {reset && (
            <button
              onClick={reset}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-colors"
            >
              <RefreshCw size={16} />
              Try Again
            </button>
          )}
          <Link
            href="/welcome"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-border bg-muted/50 text-foreground font-semibold text-sm hover:bg-muted/80 transition-colors"
          >
            <Home size={16} />
            Back to Home
          </Link>
        </div>

        <p className="mt-8 text-xs text-muted-foreground/50">
          Error reference: {error?.digest || 'ERR_500'}
        </p>
      </div>
    </div>
  );
}
